import React, { useState, useEffect } from 'react';
import parse from 'html-react-parser';

import formLang from '../../../controllers/language/shared/form';
import generalController from '../../../controllers/generalController';
import { formatDate, pushToGTMDataLayer } from '../../../utils/helpers';
import { handleRequest } from '../../../utils/httpRequest';
import { languageController } from '../../../controllers/language/languageController';
import { useForm } from '../../../hooks/useForm';

import Select from '../../1_Atoms/Forms/Select/Select';
import Textarea from '../../1_Atoms/Forms/Textarea/Textarea';
import Checkbox from '../../1_Atoms/Forms/Checkbox/Checkbox';
import Input from '../../1_Atoms/Forms/Input/Input';
import Button from '../../1_Atoms/Button/Button';
import FormGroup from '../../2_Molecules/FormGroup/FormGroup';
import Form from '../../2_Molecules/Form/Form';
import CalloutBoxFull from '../../2_Molecules/CalloutBox/CalloutBoxFull';
import FormAndCalloutBox from '../../3_Organisms/FormAndCalloutBox/FormAndCalloutBox'

interface DealerGeolocation {
  distance: number;
  latitude: number;
  longitude: number;
}

interface Dealer {
  name: string;
  address_line_1: string;
  city: string;
  post_code: string;
  website?: string;
  phone_number?: string;
  geolocation: DealerGeolocation;
  dealer_code?: string;
}

interface DealerData {
  dealer?: Dealer;
  userPosition?: UserPosition;
}

interface UserPosition {
  lat: number;
  lng: number;
}

interface DateAndTimeData {
  date: string;
  time: string;
}

interface VehicleData {
  title: string;
  model: string;
  year?: string;
  modelYear: string;
  description?: string;
  msrp?: number;
}

interface ContactFormData {
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  message: string;
  optin: string;
  phone: string;
  preferredContact: string;
  [key: string]: string | number | boolean;
}

interface FormValidations {
  [key: string]: string[] | undefined;
}

interface ApiResponse {
  Success: boolean;
  state?: string;
  Errors?: string[];
}

interface OtherFormData {
  source: string;
  language: string;
  pid: string;
  dealerid?: string;
  year?: string;
  model?: string;
  date?: string;
  time?: string;
  [key: string]: string | undefined;
}

interface ContactDetailsStepProps {
  handleNextStep: (data: { contactDetails: ContactFormData }) => void;
  title?: string;
  submitButtonText?: string;
  dealerData?: DealerData;
  dateAndTimeData?: DateAndTimeData;
  vehicleData?: VehicleData;
  formSource: string;
  formPid: number;
  formEndPointUrl: string;
  formLanguage: string;
  formDisclaimer: string;
}

const ContactDetailsStep: React.FC<ContactDetailsStepProps> = ({
  handleNextStep,
  title,
  submitButtonText = '',
  dealerData,
  dateAndTimeData,
  vehicleData,
  formSource,
  formPid,
  formEndPointUrl,
  formLanguage,
  formDisclaimer
}) => {
  const [formError, setFormError] = useState<string | null>(null)

  const { dealer: selectedDealerData, userPosition } = dealerData || {};

  const language = generalController.detectLanguage();
  
  const initState: ContactFormData = {
    firstname: '',
    lastname: '',
    email: '',
    postalcode: '',
    message: '',
    optin: 'off', // 'on' or 'off'
    phone:'',
    preferredContact:'Email',
  }

  const [phoneValue, setPhoneValue] = useState<string>('')
  const [validations, setValidations] = useState<FormValidations>({
    firstname: ['required', 'name', 'nameMaxMin'],
    lastname: ['required', 'name', 'nameMaxMin'],
    phone:['phone'],
    email: ['required', 'email'],
    postalcode: ['required', 'postalcode'],
  })

  const { handleSubmit, handleChange, data: formData, errors, setData } = useForm({
    initState,
    validations: validations as any,
    // Send the form data if validated
    onSubmit: async () => {
      try {
        const otherData: OtherFormData = {
          source: formSource,
          language: formLanguage,
          pid: String(formPid)
        }
        
        // Add selectedDealerData data if passed
        if (selectedDealerData) {
          otherData.dealerid = selectedDealerData.dealer_code
        }

        // Add vehicleData data if passed
        if (vehicleData) {
          otherData.year = vehicleData.year;
          otherData.model = vehicleData.model;
        }

        // Add Date and Time data if passed
        if (dateAndTimeData) {
          otherData.date = formatDate(new Date(dateAndTimeData.date))
          otherData.time = dateAndTimeData.time
        }

        // Remove whitespace in postalcode
        if (typeof formData.postalcode === 'string' && formData.postalcode) {
          formData.postalcode = formData.postalcode.replace(/ /g, "");
        }
        if(typeof formData.phone === 'string' && formData.phone.length > 10){
          formData.phone = formData.phone.replace(/\D/g, '');
        }

        const res = await handleRequest('post', formEndPointUrl, { ...formData, ...otherData }) as ApiResponse;

        if (res.Success) {
          pushToGTMDataLayer({ formevent: 'submit' });
          handleNextStep({ contactDetails: formData as ContactFormData });
        } else {
          setFormError(res.state ? res.state : (res.Errors && res.Errors[0] ? res.Errors[0] : 'An error occurred'))
        }
      } catch (err) {
        console.log(err)
      }
    },
  })

  const { firstname, lastname, email, postalcode, phone, preferredContact, message, optin } = formData;

  const isFormFilled = (firstname && lastname && email && postalcode)

  useEffect(() => {
    setValidations(prevValidations => {
      const newValidations = { ...prevValidations }; // Create a new object copy
      if (phoneValue.length > 9) {
        // Add 'required' rule to preferredContact
        newValidations.preferredContact = ['required'];
      } else {
        // Remove preferredContact rule
        if (newValidations.preferredContact) {
          delete newValidations.preferredContact;
        }
      }
      return newValidations;
    });
  }, [phoneValue])

  return (
    <div className="mz-contact-details-step">
      <div className="mz-contact-details-step__top">
        <div className="mz-container">
          <div className="mz-full-width">
            <h5 className="mz-contact-details-step__title">{title}</h5>
          </div>
        </div>
      </div>
      <FormAndCalloutBox>
        <FormAndCalloutBox.Form>
          <Form
            type="contact-details"
            onSubmit={handleSubmit}
            isDark={false}
            noValidate={true}
            requiredFieldsHelperText={true}
            errorMessage={formError || undefined}
          >
            <FormGroup md={2}>
              <Input
                type="text"
                name="firstname"
                label={languageController.getFormCopy('label', 'firstname') || ''}
                value={firstname as string}
                isRequired={true}
                errorMessage={errors.firstname}
                onChange={handleChange}
                maxLength={50}
              />
              <Input
                type="text"
                name="lastname"
                label={languageController.getFormCopy('label', 'lastname') || ''}
                value={lastname as string}
                isRequired={true}
                errorMessage={errors.lastname}
                onChange={handleChange}
                maxLength={50}
              />
            </FormGroup>
            <Input
              type="email"
              name="email"
              label={languageController.getFormCopy('label', 'email') || ''}
              value={email as string}
              isRequired={true}
              errorMessage={errors.email}
              onChange={handleChange}
              maxLength={320}
            />
            <FormGroup md={2}>
              <Input
                type="text"
                name="postalcode"
                label={languageController.getFormCopy('label', 'postalcode') || ''}
                value={postalcode as string}
                isRequired={true}
                errorMessage={errors.postalcode}
                onChange={handleChange}
                maxLength={7}
              />
              {/*<Input
                type="text"
                name="phone"
                label={languageController.getFormCopy('label', 'phone') || ''}
                id="contact-phone"
                value={phone as string}
                isRequired={false}
                errorMessage={errors.phone}
                onChange={(e)=>{
                  handleChange(e)
                  if(phoneValue != e.target.value)setPhoneValue(e.target.value.replace(/\D/g, ''))
                }}
                maxLength={14}
              />*/}
            </FormGroup>
            {/*phoneValue.length > 9 &&
              <FormGroup md={2}>
                <Select
                  name="preferredContact"
                  label={languageController.getFormCopy('label', 'preferredContact') || ''}
                  id="contact-preferredContact"
                  value={preferredContact as string}
                  isRequired={true}
                  errorMessage={errors.preferredContact}
                  onChange={handleChange}
                  placeholder={languageController.getFormCopy('placeholder', 'preferredContact') || ''}
                  options={formLang.select.preferredContact.map(subject => ({
                    value: subject[language],
                    label: subject[language],
                  }))}
                />
              </FormGroup>*/
            }
            <Textarea
                name="message"
                label={languageController.getFormCopy('label', 'message') || ''}
                id="contact-message"
                value={message as string}
                isRequired={false}
                errorMessage={errors.message}
                onChange={handleChange}
                maxLength={1999}
                rows={5}
              />
            <Checkbox
              name="optin"
              checked={(optin === 'on')}
              onChange={(e) => setData({ ...formData, optin: optin === 'off' ? 'on' : 'off' })}
              label={formDisclaimer ? parse(formDisclaimer) : ''}
            />
            <Button
              type="submit"
              className="mz-btn--primary mz-btn--red"
              label={submitButtonText}
              disabled={!isFormFilled}
            />
          </Form>
        </FormAndCalloutBox.Form>
        <FormAndCalloutBox.Callout>
          <CalloutBoxFull
            vehicleData={vehicleData || undefined}
            dealerData={selectedDealerData}
            userPosition={userPosition}
            dateAndTimeData={dateAndTimeData || undefined}
          />
        </FormAndCalloutBox.Callout>
      </FormAndCalloutBox>
    </div>
  )
}

export default ContactDetailsStep
